// Opt-in Core API canary override.
//
// Activation order:
// - Localhost UI only:
//   1) ?apiBase=... (optional; persisted to localStorage)
//   2) localStorage.KG_API_BASE_URL
// - Fallback:
//   - default environment routing (api-staging / api)
//
// Safety:
// - Overrides are localhost only (staging/production are inert).
// - Only allows a small allowlist of domains (no execute-api).

import { isLocalLoopbackHostname } from './companyTenantResolver';

const OVERRIDE_KEY = 'KG_API_BASE_URL';
const QUERY_PARAM = 'apiBase';

const STAGING_ALLOWED_ORIGINS = new Set([
  'https://core2-staging.krossglobalmarketing.com',
  'https://api-staging.krossglobalmarketing.com',
]);

let cachedInfo = null;

function isBrowser() {
  return typeof window !== 'undefined' && !!window.location;
}

function getUiHostKind() {
  if (!isBrowser()) return false;
  const hostname = String(window.location.hostname || '').toLowerCase();
  if (!hostname) return false;
  if (isLocalLoopbackHostname(hostname)) return 'localhost';
  if (hostname === 'staging.krossglobalmarketing.com' || hostname.includes('staging')) return 'staging';
  if (hostname === 'krossglobalmarketing.com' || hostname === 'www.krossglobalmarketing.com') return 'production';
  return 'other';
}

function normalizeToOrigin(raw) {
  const s0 = String(raw || '').trim();
  if (!s0) return null;
  const withProto = /^https?:\/\//i.test(s0) ? s0 : `https://${s0}`;
  try {
    const u = new URL(withProto);
    // Force origin only (drops any path/query fragments).
    return u.origin;
  } catch {
    return null;
  }
}

function stripQueryParamFromUrl(paramName) {
  try {
    const u = new URL(window.location.href);
    if (!u.searchParams.has(paramName)) return;
    u.searchParams.delete(paramName);
    window.history.replaceState({}, '', u.toString());
  } catch {
    // ignore
  }
}

function computeOverrideInfo() {
  if (!isBrowser()) return { active: false };

  const kind = getUiHostKind();

  // Guardrail: overrides are localhost only.
  // Staging and production should never read query params/localStorage for core API routing.
  if (kind !== 'localhost') return { active: false };

  const currentUrl = new URL(window.location.href);

  const allowedOrigins = STAGING_ALLOWED_ORIGINS;

  // 1) Query param (persist to localStorage for convenience)
  const qp = currentUrl.searchParams.get(QUERY_PARAM);
  if (qp) {
    const origin = normalizeToOrigin(qp);
    stripQueryParamFromUrl(QUERY_PARAM);
    if (origin && allowedOrigins.has(origin)) {
      try {
        localStorage.setItem(OVERRIDE_KEY, origin);
      } catch {
        // ignore storage errors
      }
    } else {
      // eslint-disable-next-line no-console
      console.warn('[KG] Ignoring apiBase override (not allowlisted):', qp);
    }
  }

  // 2) localStorage
  let origin = null;
  try {
    origin = normalizeToOrigin(localStorage.getItem(OVERRIDE_KEY));
  } catch {
    origin = null;
  }

  if (!origin || !allowedOrigins.has(origin)) {
    return { active: false };
  }

  // Effective base URL used by RTK Query core routes
  const coreBaseUrl = `${origin}/core`;
  const isCanary = origin === 'https://core2-staging.krossglobalmarketing.com';

  return {
    active: isCanary,
    origin,
    coreBaseUrl,
    source: 'localStorage',
  };
}

export function getCoreApiOverrideInfo() {
  if (cachedInfo) return cachedInfo;
  cachedInfo = computeOverrideInfo();
  return cachedInfo;
}

export function disableCoreApiOverride() {
  if (!isBrowser()) return;
  try {
    localStorage.removeItem(OVERRIDE_KEY);
  } catch {
    // ignore
  }
  stripQueryParamFromUrl(QUERY_PARAM);
  // Hard reload so all in-flight RTKQ requests re-resolve base URL.
  window.location.reload();
}

